import { Box, Stack, Typography } from "@mui/material";
import { getCoordinatesDisplayValue } from "../../../../formatting/domain-value-manipulation";
import Reference from "../../common/Reference";
import Section from "../../common/Section/Section";
import { Field, SectionComponentProps } from "../../types";
import MapExampleImage from "../references/satellite-visual.png";
import LocationPinMapExampleImage from "../references/location-pin-map.jpg";
import { buildSatelliteImageryFields } from "../../../fields/satelliteImagery";
import { buildLocationPinMapField } from "../../../fields/locationPinMap";

function MapReference({
  image,
  alt,
  testId,
  guidance,
}: {
  image: string;
  alt: string;
  testId: string;
  guidance: string;
}) {
  return (
    <Stack direction={"column"} gap={0} sx={{ width: "100%" }}>
      <Reference image={image} radius="25px" alt={alt} testId={testId} />
      <Box
        sx={{
          backgroundColor: "background.default",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Typography
          sx={{
            pl: 2,
            pt: 0.5,
            pr: 1.5,
            fontWeight: 900,
            fontSize: "0.7rem",
            width: "100%",
          }}
        >
          {guidance}
        </Typography>
      </Box>
    </Stack>
  );
}

function MapInformation({ details, onEdit }: SectionComponentProps) {
  const { satelliteImagery, coordinates, locationPinMapLink } = details;
  const satelliteImageryFields = buildSatelliteImageryFields(
    satelliteImagery,
    1,
  );
  const mapFields: Array<Field> = [
    {
      name: "Coordinates",
      type: "text",
      index: 0,
      fullLine: true,
      value: getCoordinatesDisplayValue(coordinates),
    },
    ...satelliteImageryFields,
  ];
  const locationPinMapFields = [
    buildLocationPinMapField(
      locationPinMapLink,
      satelliteImageryFields.length + 1,
    ),
  ];

  return (
    <Section
      title="Map information"
      columns={2}
      onEdit={() => onEdit("map-information")}
      rows={[
        {
          fields: mapFields,
          reference: (
            <MapReference
              image={MapExampleImage}
              alt="Map example reference"
              testId="map-example-reference"
              guidance="Research to create satellite visuals for the project (gifs or maps) (e.g. deforestation over time, NDVI change over time, project area change) and upload them to the Project Visuals folder. Caption should communicate the context of the map in the simplest way possible. Add a map description under the image, including the used dataset."
            />
          ),
        },
        {
          fields: locationPinMapFields,
          reference: (
            <MapReference
              image={LocationPinMapExampleImage}
              alt="Location pin map example reference"
              testId="location-pin-map-reference"
              guidance="Capture a wider map showing where the project sits in its region, with a single pin on the project location, and upload it to the Project Visuals folder. The link should point at that image."
            />
          ),
        },
      ]}
    />
  );
}

export default MapInformation;
